import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Loader2, Mail, Megaphone, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function EmailPreferences() {
  const [prefs, setPrefs] = useState({ boost_updates: true, marketing: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/me/email-prefs")
      .then((r) => setPrefs(r.data))
      .catch(() => toast.error("Impossible de charger vos préférences"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/me/email-prefs", prefs);
      toast.success("Préférences enregistrées");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-12"><Loader2 className="animate-spin h-6 w-6" /></div>;

  const items = [
    {
      key: "boost_updates",
      icon: TrendingUp,
      label: "Notifications de boost",
      desc: "Activation, mi-parcours et expiration des boosts régionaux que vous avez achetés. Recommandé pour suivre votre ROI.",
    },
    {
      key: "marketing",
      icon: Megaphone,
      label: "Sélections et nouveautés",
      desc: "Email mensuel « Best-of », nouveaux événements éditoriaux et opportunités exclusives. Désactivable à tout moment.",
    },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center gap-3 mb-2">
        <Mail className="h-5 w-5 text-accent" />
        <p className="text-label text-muted-foreground">Confidentialité & RGPD</p>
      </div>
      <h1 className="font-heading font-light tracking-editorial text-4xl text-ink mb-3">
        Préférences <em className="font-display text-accent">email</em>
      </h1>
      <p className="text-muted-foreground mb-10 max-w-2xl text-sm leading-relaxed">
        Choisissez les emails que vous souhaitez recevoir. Les emails essentiels (création de compte, confirmation de paiement, notifications de réservation) restent activés conformément à notre intérêt légitime.
      </p>

      <div className="space-y-4 mb-10">
        {items.map(({ key, icon: Icon, label, desc }) => (
          <div
            key={key}
            data-testid={`pref-row-${key}`}
            className="bg-white border border-ink/10 rounded-md p-5 flex items-start gap-4"
          >
            <div className="bg-cream-100 p-2.5 rounded-md">
              <Icon className="h-4 w-4 text-ink/70" />
            </div>
            <div className="flex-1 min-w-0">
              <Label htmlFor={`switch-${key}`} className="font-heading font-medium text-base text-ink cursor-pointer">
                {label}
              </Label>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{desc}</p>
            </div>
            <Switch
              id={`switch-${key}`}
              data-testid={`switch-${key}`}
              checked={prefs[key]}
              onCheckedChange={(v) => setPrefs((p) => ({ ...p, [key]: v }))}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-6 border-t border-ink/10">
        <p className="text-xs text-muted-foreground max-w-md">
          Conformément au RGPD, vous pouvez aussi vous désinscrire en un clic depuis n'importe quel email reçu.
        </p>
        <Button
          onClick={save}
          disabled={saving}
          data-testid="prefs-save"
          className="bg-ink hover:bg-ink/85 text-cream rounded-none px-6"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}
